/**
 * useHybridSave Hook
 *
 * Implements hybrid save strategy:
 * - Auto-save for simple fields (name, phone, description, hourlyRate, workingHours)
 * - Manual save for critical fields (categories, location, services)
 * - Unsaved changes warning for critical fields
 */

'use client';

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { ProfileFormData } from './useProfileForm';
import type { CategoryWithSubcategories } from './useCategoryManagement';
import type { WorkingHoursDay } from '../WorkingHoursSection';

export type AutoSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

interface SimpleFieldsData {
  firstName: string;
  lastName: string;
  phone: string;
  profileDescription: string;
  hourlyRate: string;
  showLocation: boolean;
  workingHours: WorkingHoursDay[];
}

interface CriticalFieldsData {
  address: string;
  city: string;
  province: string;
  postalCode: string;
  latitude: number | null;
  longitude: number | null;
  serviceArea: string;
  categories: CategoryWithSubcategories[];
}

export interface UseHybridSaveOptions {
  formData: ProfileFormData;
  selectedCategoryGroups: CategoryWithSubcategories[];
  workingHours: WorkingHoursDay[];
  initialFormData: ProfileFormData | null;
  initialCategories: CategoryWithSubcategories[];
  initialWorkingHours: WorkingHoursDay[];
  onAutoSave: (data: SimpleFieldsData) => Promise<void>;
  onManualSave: () => Promise<void>;
  debounceMs?: number;
  enabled?: boolean;
}

export interface UseHybridSaveReturn {
  /** Auto-save status for simple fields */
  autoSaveStatus: AutoSaveStatus;
  /** Whether there are unsaved critical changes */
  hasUnsavedCriticalChanges: boolean;
  /** Whether unsaved changes dialog is open */
  isDialogOpen: boolean;
  /** Open the unsaved changes dialog */
  openDialog: () => void;
  /** Close the dialog and cancel navigation */
  closeDialog: () => void;
  /** Save critical changes and continue navigation */
  saveAndContinue: () => Promise<void>;
  /** Discard changes and continue navigation */
  discardAndContinue: () => void;
  /** Mark critical fields as saved (call after successful manual save) */
  markCriticalAsSaved: () => void;
  /** Trigger auto-save immediately */
  saveSimpleNow: () => Promise<void>;
  /** Cancel pending auto-save */
  cancelAutoSave: () => void;
}

export function useHybridSave({
  formData,
  selectedCategoryGroups,
  workingHours,
  initialFormData,
  initialCategories,
  initialWorkingHours,
  onAutoSave,
  onManualSave,
  debounceMs = 2000,
  enabled = true,
}: UseHybridSaveOptions): UseHybridSaveReturn {
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // Counter to force re-evaluation of hasUnsavedCriticalChanges after markCriticalAsSaved
  const [criticalSaveCounter, setCriticalSaveCounter] = useState(0);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const pendingNavigationRef = useRef<(() => void) | null>(null);

  // Track last saved values for simple fields
  const lastSavedSimpleRef = useRef<SimpleFieldsData | null>(null);
  // Track last saved values for critical fields
  const lastSavedCriticalRef = useRef<CriticalFieldsData | null>(null);

  // Initialize saved values from initial data
  useEffect(() => {
    if (initialFormData && !lastSavedSimpleRef.current) {
      lastSavedSimpleRef.current = {
        firstName: initialFormData.firstName,
        lastName: initialFormData.lastName,
        phone: initialFormData.phone,
        profileDescription: initialFormData.profileDescription,
        hourlyRate: initialFormData.hourlyRate,
        showLocation: initialFormData.showLocation,
        workingHours: initialWorkingHours,
      };
    }
    if (initialFormData && !lastSavedCriticalRef.current) {
      lastSavedCriticalRef.current = {
        address: initialFormData.address,
        city: initialFormData.city,
        province: initialFormData.province,
        postalCode: initialFormData.postalCode,
        latitude: initialFormData.latitude,
        longitude: initialFormData.longitude,
        serviceArea: initialFormData.serviceArea,
        categories: initialCategories,
      };
    }
  }, [initialFormData, initialCategories, initialWorkingHours]);

  // Current simple fields data
  const currentSimpleData: SimpleFieldsData = useMemo(() => ({
    firstName: formData.firstName,
    lastName: formData.lastName,
    phone: formData.phone,
    profileDescription: formData.profileDescription,
    hourlyRate: formData.hourlyRate,
    showLocation: formData.showLocation,
    workingHours,
  }), [formData, workingHours]);

  // Current critical fields data
  const currentCriticalData: CriticalFieldsData = useMemo(() => ({
    address: formData.address,
    city: formData.city,
    province: formData.province,
    postalCode: formData.postalCode,
    latitude: formData.latitude,
    longitude: formData.longitude,
    serviceArea: formData.serviceArea,
    categories: selectedCategoryGroups,
  }), [formData, selectedCategoryGroups]);

  // Check if simple fields have changed
  const hasSimpleChanges = useMemo(() => {
    if (!lastSavedSimpleRef.current) return false;
    return JSON.stringify(currentSimpleData) !== JSON.stringify(lastSavedSimpleRef.current);
  }, [currentSimpleData]);

  // Check if critical fields have changed
  // criticalSaveCounter forces re-evaluation after markCriticalAsSaved updates the ref
  const hasUnsavedCriticalChanges = useMemo(() => {
    if (!lastSavedCriticalRef.current) return false;
    return JSON.stringify(currentCriticalData) !== JSON.stringify(lastSavedCriticalRef.current);
  }, [currentCriticalData, criticalSaveCounter]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Browser beforeunload warning for critical changes
  useEffect(() => {
    if (!hasUnsavedCriticalChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedCriticalChanges]);

  // Perform auto-save
  const performAutoSave = useCallback(async () => {
    if (!isMountedRef.current || !enabled) return;

    setAutoSaveStatus('saving');

    try {
      await onAutoSave(currentSimpleData);

      if (isMountedRef.current) {
        lastSavedSimpleRef.current = currentSimpleData;
        setAutoSaveStatus('saved');

        // Reset to idle after showing "saved" status
        setTimeout(() => {
          if (isMountedRef.current) {
            setAutoSaveStatus('idle');
          }
        }, 2000);
      }
    } catch (err) {
      if (isMountedRef.current) {
        console.error('[useHybridSave] Auto-save failed:', err);
        setAutoSaveStatus('error');
      }
    }
  }, [currentSimpleData, onAutoSave, enabled]);

  // Debounced auto-save for simple fields
  useEffect(() => {
    if (!enabled || !hasSimpleChanges || !lastSavedSimpleRef.current) {
      return;
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setAutoSaveStatus('pending');

    timeoutRef.current = setTimeout(() => {
      performAutoSave();
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currentSimpleData, enabled, hasSimpleChanges, debounceMs, performAutoSave]);

  const saveSimpleNow = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    await performAutoSave();
  }, [performAutoSave]);

  const cancelAutoSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setAutoSaveStatus('idle');
  }, []);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    pendingNavigationRef.current = null;
  }, []);

  const saveAndContinue = useCallback(async () => {
    try {
      await onManualSave();
      lastSavedCriticalRef.current = currentCriticalData;
      setIsDialogOpen(false);
      pendingNavigationRef.current?.();
      pendingNavigationRef.current = null;
    } catch (err) {
      console.error('[useHybridSave] Manual save failed:', err);
    }
  }, [onManualSave, currentCriticalData]);

  const discardAndContinue = useCallback(() => {
    setIsDialogOpen(false);
    pendingNavigationRef.current?.();
    pendingNavigationRef.current = null;
  }, []);

  const markCriticalAsSaved = useCallback(() => {
    lastSavedCriticalRef.current = currentCriticalData;
    // Increment counter to force re-evaluation of hasUnsavedCriticalChanges
    setCriticalSaveCounter((c) => c + 1);
  }, [currentCriticalData]);

  return {
    autoSaveStatus,
    hasUnsavedCriticalChanges,
    isDialogOpen,
    openDialog,
    closeDialog,
    saveAndContinue,
    discardAndContinue,
    markCriticalAsSaved,
    saveSimpleNow,
    cancelAutoSave,
  };
}
